import AdminPublicLayout from "@/layouts/AdminPublicLayout";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  Users,
  BarChart3,
  ArrowRight,
  CheckCircle, Link, Building
} from "lucide-react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { useState } from "react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import ContactDialog from "@/components/dialogs/ContactDialog";

const Index = () => {
  const { t } = useTranslation();
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <AdminPublicLayout>
      <Helmet>
        <title>FitCalendar Pricing - Affordable Yoga & Fitness Booking Software</title>
        <link rel="canonical" href="https://fitcalendar.praxipro.io/pricing/" />
        <meta name="title" content="FitCalendar Pricing - Affordable Yoga & Fitness Booking Software" />
        <meta name="description" content="Discover FitCalendar’s flexible pricing plans designed for yoga, Pilates, CANTIENICA®, and fitness studios. Start your free trial and grow your studio today." />
        <meta name="author" content="PraxiPro" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fitcalendar.praxipro.io/pricing/" />
        <meta property="og:title" content="FitCalendar Pricing - Affordable Yoga & Fitness Booking Software" />
        <meta property="og:description" content="Choose the perfect plan for your yoga, Pilates, CANTIENICA®, or fitness studio. Affordable pricing, easy setup, and powerful booking features with FitCalendar." />
        <meta property="og:image" content="/og-image.png" />
        <meta property="og:site_name" content="FitCalendar Studio Software" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://fitcalendar.praxipro.io/pricing/" />
        <meta name="twitter:title" content="FitCalendar Pricing - Affordable Yoga & Fitness Booking Software" />
        <meta name="twitter:description" content="Explore FitCalendar pricing plans for yoga, Pilates, and fitness studios. Start your free trial and simplify your booking and scheduling." />
        <meta name="twitter:image" content="/og-image.png" />
        <meta name="twitter:creator" content="@PraxiPro" />

        <meta itemProp="name" content="FitCalendar Pricing - Affordable Yoga & Fitness Booking Software" />
        <meta itemProp="description" content="Flexible and affordable pricing plans for yoga, Pilates, CANTIENICA®, and fitness studios. Start accepting bookings online with FitCalendar today." />
        <meta itemProp="image" content="/og-image.png" />
        
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('pricing.header')}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('pricing.subHeader')}
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-center gap-8">
            {/* Free Plan */}
            <Card className="w-full lg:w-1/3 border-gray-200 hover:border-gray-300 transition-all">
              <CardHeader className="text-center pb-0">
                <CardTitle className="text-2xl font-bold">{t('pricing.freeTrial')}</CardTitle>
                <div className="mt-3 mb-1">
                  <span className="text-3xl font-bold">€0</span>
                  <span className="text-gray-500 ml-1">{t('pricing.perMonth')}</span>
                </div>
                <p className="text-gray-500">{t('pricing.freeTrialDesc')}</p>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {(t('pricing.freeTrialFeatures', { returnObjects: true }) as string[]).map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button variant="outline" className="w-full" onClick={() => setIsContactOpen(true)}>{t('pricing.startFreeTrial')}</Button>
              </CardFooter>
            </Card>

            {/* Monthly Plan */}
            <Card className="w-full lg:w-1/3 border-primary bg-gradient-to-b from-blue-50 to-white shadow-lg relative">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-primary text-white text-sm font-bold py-1 px-4 rounded-full">
                {t('pricing.popularPlan')}
              </div>
              <CardHeader className="text-center pb-0">
                <CardTitle className="text-2xl font-bold">{t('pricing.standard')}</CardTitle>
                <div className="mt-3 mb-1">
                  <span className="text-4xl font-bold">€20</span>
                  <span className="text-gray-500 ml-1">{t('pricing.perMonth')}</span>
                </div>
                <p className="text-gray-500">{t('pricing.standardDesc')}</p>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {(t('pricing.standardFeatures', { returnObjects: true }) as string[]).map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span dangerouslySetInnerHTML={{ __html: feature }} />
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button className="w-full" onClick={() => setIsContactOpen(true)}>{t('pricing.subscribeNow')}</Button>
              </CardFooter>
            </Card>

            {/* Annual Plan */}
            {/* <Card className="w-full lg:w-1/3 border-gray-200 hover:border-gray-300 transition-all">
                <CardHeader className="text-center pb-0">
                  <CardTitle className="text-2xl font-bold">{t('annual')}</CardTitle>
                  <div className="mt-3 mb-1">
                    <span className="text-3xl font-bold">€192</span>
                    <span className="text-gray-500 ml-1">{t('perYear')}</span>
                  </div>
                  <p className="text-gray-500">{t('annualDesc')}</p>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3">
                    {(t('annualFeatures') as string[]).map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                        <span dangerouslySetInnerHTML={{ __html: feature }} />
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" className="w-full">{t('subscribeAnnually')}</Button>
                </CardFooter>
              </Card> */}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">{t('pricing.faqsTitle')}</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-3">{t('pricing.faqsQ1')}</h3>
              <p className="text-gray-600">{t('pricing.faqsA1')}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-3">{t('pricing.faqsQ2')}</h3>
              <p className="text-gray-600">{t('pricing.faqsA2')}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-3">{t('pricing.faqsQ3')}</h3>
              <p className="text-gray-600">{t('pricing.faqsA3')}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-3">{t('pricing.faqsQ4')}</h3>
              <p className="text-gray-600">{t('pricing.faqsA4')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-secondary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            {t("landing.ctaSectionTitle")}
          </h2>
          <p className="text-lg mb-8 opacity-90">
            {t("landing.ctaSectionDescription")}
          </p>
          <Button
            size="lg"
            variant="outline"
            className="bg-white text-primary hover:bg-gray-100"
            onClick={() => setIsContactOpen(true)}
          >
            {t("landing.ctaText")}
          </Button>
        </div>
      </section>

      <ContactDialog
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
      />
    </AdminPublicLayout>
  );
};

export default Index;
